/**
 * DocumentsWithInbox (PROD-3274 DF-04): the patient chart's Documents tab with the F1 "Documents view" segment on top.
 * "Inbox · N" shows the intake inbox; "Filed documents" is today's list, unchanged.
 *
 * With intake off (`isIntakeOn()`: the deploy flag and the browser's `?x=intake` switch; `?x=chart-intake` also shows the view and sends the chart's "Add document" to the drop zone) this renders `DocumentsChartTab` alone: no segment, no request. When the server
 * answers 404 `intake_disabled`, the segment goes away and Filed documents stays.
 */
import { useCallback, useState } from 'react';
import { Banner, Segmented, Skeleton } from '@scripsteam/scrips-signal-ds';
import { isChartIntakeOn, isIntakeOn } from '../lib/intake-switch';
import { DocumentsChartTab, type DocumentsChartTabProps } from '@/patients/components/chart/documents-chart-tab';
import { IntakeApiError } from '../api/intake-client';
import { useIntakeInbox } from '../hooks/use-intake-inbox';
import { IntakeInbox } from './intake-inbox';

export interface DocumentsWithInboxProps extends DocumentsChartTabProps {
  /** For the drop zone and the "This is <first name>" button. */
  patientFirstName?: string | null;
}

type View = 'inbox' | 'filed';

function InboxSwitch({ patientFirstName, ...tab }: DocumentsWithInboxProps & { patientId: string }) {
  // PROD-3204 (`?x=chart-intake`): the chart's "Add document" shows the Inbox and opens the drop zone's file picker.
  const chartIntake = isChartIntakeOn();
  const [pickRequested, setPickRequested] = useState(false);
  // The opening view is decided ONCE, when the first inbox read settles: the Inbox when files are waiting, else Filed
  // documents (an empty or failed inbox never replaces the filed list). Until then a skeleton holds the place, so the
  // view never switches under a clinician who started reading. After that only the clinician changes it.
  const [view, setView] = useState<View | null>(null);
  const inbox = useIntakeInbox(tab.patientId);
  const disabled = inbox.error instanceof IntakeApiError && inbox.error.code === 'intake_disabled';
  const settled = inbox.isFetched || inbox.isError;
  const count = inbox.data?.length;
  if (view === null && settled && !disabled) setView(count ? 'inbox' : 'filed');

  const addDocument = useCallback(() => {
    setView('inbox');
    setPickRequested(true);
  }, []);
  const filedTab = chartIntake ? { ...tab, onAddDocument: addDocument } : tab;

  if (disabled) {
    return chartIntake ? (
      <div className="flex flex-col">
        <div className="px-6 pt-4">
          <Banner variant="info">Document Intake is off for this clinic, so Add document uses the file upload.</Banner>
        </div>
        <DocumentsChartTab {...tab} />
      </div>
    ) : (
      <DocumentsChartTab {...tab} />
    );
  }
  return (
    <div className="flex flex-col">
      <div data-slot="intake-view-bar" className="px-6 pt-4">
        <Segmented<View>
          aria-label="Documents view"
          variant="pill"
          value={view ?? 'filed'}
          onChange={setView}
          // Not choosable until the opening view is decided (the skeleton below shows meanwhile).
          options={[
            { value: 'inbox', label: count === undefined ? 'Inbox' : `Inbox · ${count}`, disabled: view === null },
            { value: 'filed', label: 'Filed documents', disabled: view === null },
          ]}
        />
        <span data-slot="intake-quiet">
          Nothing in the inbox is part of the record until someone adds it.
        </span>
      </div>
      {view === null ? (
        <div aria-busy="true" aria-label="Loading documents" className="flex flex-col gap-2 p-6">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : view === 'inbox' ? (
        <div className="p-6">
          <IntakeInbox
            patientId={tab.patientId}
            patientFirstName={patientFirstName?.trim() || 'the patient'}
            openPicker={pickRequested}
            onPickerOpened={() => setPickRequested(false)}
          />
        </div>
      ) : (
        <DocumentsChartTab {...filedTab} />
      )}
    </div>
  );
}

export function DocumentsWithInbox(props: DocumentsWithInboxProps) {
  if (!(isIntakeOn() || isChartIntakeOn()) || !props.patientId) {
    const tab: DocumentsChartTabProps = { ...props };
    delete (tab as DocumentsWithInboxProps).patientFirstName;
    return <DocumentsChartTab {...tab} />;
  }
  return <InboxSwitch {...props} patientId={props.patientId} />;
}
